import { Flex } from "styled-system/jsx";
import {
  DatesAndGuests,
  CustomerDetails,
  PaymentMethod,
  DeliveryMethod,
  Policies,
  AddOns,
  Purchase,
  Questionnaire,
  DynamicFeeQuestionnaire,
  OperatorSelect,
  TierSelection,
  MerchVariantSelection,
  EventQuantityStep,
  EventSessionPickerStep,
  EventTierSelectorStep,
  InvoiceBillingStep,
} from "./components";
import { EventGuestDetailsStep } from "./components/EventFlow/EventGuestDetailsStep";
import { EnumProductType } from "gql/generated";
import { getProductTypeConfig } from "shared/config/productTypeConfig";
import {
  getEventPurchaseFlow,
  getResolvedProductSellingConfig,
} from "shared/utils";
import {
  BookerSection,
  resolveSectionOrder,
} from "shared/utils/widgetSectionOrder";
import { Fragment, ReactNode } from "react";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { useIsBookingRequest } from "widgets/booker/shared/hooks/useIsBookingRequest";
import { useIsInvoicedAfterService } from "widgets/booker/shared/hooks/useIsInvoicedAfterService";

export const Steps = () => {
  const {
    product,
    invoiceAdminMode,
    booker,
    paymentRequestGrandTotal,
    isCatalog,
  } = useBookerContext();
  const { grandTotal, depositTotal } = useCartContext();
  const isBookingRequest = useIsBookingRequest();
  const { isInvoicedAfterService } = useIsInvoicedAfterService();

  // Blank Invoice (adhoc, line-items-only) template widgets carry a hidden
  // $0 placeholder product purely to satisfy schema requirements — the
  // admin never selects it, so skip every product-driven step and show
  // just the billing/recipient details + line items composer.
  if (invoiceAdminMode && booker?.isInvoiceTemplate) {
    return (
      <Flex flexDirection={"column"} gap={"600"}>
        <InvoiceBillingStep />
        <OperatorSelect />
      </Flex>
    );
  }

  const productTypeConfig = getProductTypeConfig(product?.type);
  const isRenewableFee = product?.type === EnumProductType.RenewableFee;
  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );
  const hasChoices = sellingConfig.options.length > 0;
  const totalDue =
    paymentRequestGrandTotal != null
      ? paymentRequestGrandTotal
      : !isRenewableFee && booker?.paymentMetadata?.depositSettings?.enabled
        ? depositTotal
        : grandTotal;
  const showPaymentMethod =
    !invoiceAdminMode &&
    !isBookingRequest &&
    !isInvoicedAfterService &&
    totalDue > 0;
  const dynamicFeeSurveyId = (
    product as { sellingConfig?: { dynamicFeeSurvey?: string | null } | null }
  )?.sellingConfig?.dynamicFeeSurvey;
  const hasDynamicFees = !!dynamicFeeSurveyId;
  // Pricing questions and the checkout questionnaire are separate surveys.
  // Skip the checkout step only when it would repeat the pricing survey.
  const checkoutSurveyIsPricingSurvey =
    !!dynamicFeeSurveyId &&
    !!booker?.survey?._id &&
    String(booker.survey._id) === String(dynamicFeeSurveyId);
  const eventFlow = getEventPurchaseFlow(
    product as typeof product & { sellingConfig?: any },
  );
  const hasMerchVariants =
    product?.type === EnumProductType.Merch &&
    ((product as any)?.merchMetadata?.variants?.length ?? 0) > 0;
  const rendersPricingQuestionnaire =
    hasDynamicFees &&
    (isRenewableFee ||
      (!hasMerchVariants &&
        product?.type !== EnumProductType.Event &&
        !productTypeConfig.hasDates));
  const showCheckoutQuestionnaire =
    !checkoutSurveyIsPricingSurvey || !rendersPricingQuestionnaire;

  const sectionOrder = resolveSectionOrder(booker?.customizations?.sectionOrder);
  const renderSections = (
    sections: Partial<Record<BookerSection, ReactNode>>,
    footer: ReactNode,
  ) => (
    <Flex flexDirection={"column"} gap={"600"}>
      {sectionOrder.map(section =>
        sections[section] ? (
          <Fragment key={section}>{sections[section]}</Fragment>
        ) : null,
      )}
      {footer}
    </Flex>
  );

  const questionnaire = showCheckoutQuestionnaire ? <Questionnaire /> : null;
  const buyerDetails = invoiceAdminMode ? (
    <InvoiceBillingStep />
  ) : (
    <CustomerDetails />
  );

  // Catalog products are picked in the browse phase; only buyer steps remain.
  if (isCatalog) {
    return renderSections(
      {
        [BookerSection.Questionnaire]: questionnaire,
        [BookerSection.CustomerDetails]: <CustomerDetails />,
        [BookerSection.Operator]: <OperatorSelect />,
        [BookerSection.AddOns]: <AddOns />,
        [BookerSection.Delivery]: <DeliveryMethod />,
      },
      <>
        {showPaymentMethod && <PaymentMethod />}
        <Policies />
      </>,
    );
  }

  const footer = (
    <>
      {showPaymentMethod && <PaymentMethod />}
      {!invoiceAdminMode && <Policies />}
    </>
  );

  if (isRenewableFee) {
    return renderSections(
      {
        [BookerSection.Product]: (hasDynamicFees || hasChoices) && (
          <>
            {hasDynamicFees && <DynamicFeeQuestionnaire />}
            {hasChoices && <TierSelection />}
          </>
        ),
        [BookerSection.Questionnaire]: questionnaire,
        [BookerSection.CustomerDetails]: buyerDetails,
        [BookerSection.Operator]: <OperatorSelect />,
        [BookerSection.AddOns]: booker?.renewableFeeAddOnsEnabled && (
          <AddOns />
        ),
      },
      footer,
    );
  }

  return renderSections(
    {
      [BookerSection.Product]: hasMerchVariants ? (
        <MerchVariantSelection />
      ) : product?.type === EnumProductType.Event ? (
        <>
          <EventQuantityStep />
          <EventSessionPickerStep />
          <EventTierSelectorStep />
          <EventGuestDetailsStep />
        </>
      ) : productTypeConfig.hasDates ? (
        <DatesAndGuests />
      ) : (
        <>
          <Purchase />
          {hasDynamicFees && <DynamicFeeQuestionnaire />}
          {hasChoices && eventFlow !== "tierSelector" && <TierSelection />}
        </>
      ),
      [BookerSection.Questionnaire]: questionnaire,
      [BookerSection.CustomerDetails]: buyerDetails,
      [BookerSection.Operator]: <OperatorSelect />,
      [BookerSection.AddOns]: <AddOns />,
      [BookerSection.Delivery]: <DeliveryMethod />,
    },
    footer,
  );
};
